import React, { useContext, useEffect, useState } from "react";
import { FeedTemplateContext } from "../../contexts/FeedTemplateProvider";
import { ContentVariation, getContent, getGalleries } from "../../services/GalleryContentService";
import PageConfig from "../../utils/PageConfig";
import { BoundedContent, Box, Card, Carousel, CarouselSlide, FluidImage, H3, Link } from "@vp/swan";
import { ProductContext } from "../../contexts/ProductContextProvider";
import { Logger } from "../../logging/logger";

export interface TemplatesGalleryProps {
  productId: string;
  mpvId: string;
  locale: string;
}

const TemplatesGallery: React.FC<TemplatesGalleryProps> = props => {
  const feedData = useContext(FeedTemplateContext);
  const { selections } = useContext(ProductContext);
  const [content, setContent] = useState<ContentVariation[]>([]);

  useEffect(() => {
    if (feedData) {
      retrieveCarouselData();
    }
  }, [feedData, selections]);

  async function retrieveCarouselData() {
    try {
      const galleries = await getGalleries(props.productId, props.locale, PageConfig.requestor);
      if (!galleries || galleries.results.length === 0) {
        setContent([]);
        return;
      }

      const galleriesPerLocale = galleries.results[0].galleryNames.filter(
        gallery => gallery.culture.toLowerCase() === props.locale.toLowerCase()
      );

      if (galleriesPerLocale.length === 0) {
        setContent([]);
        return;
      }

      const galleryName = galleriesPerLocale[0].name;

      const contentData = await getContent(
        feedData?.keywords!,
        props.mpvId,
        galleryName,
        props.locale,
        selections!,
        PageConfig.requestor
      );

      if (contentData) {
        setContent(contentData?.results.content);
      } else {
        setContent([]);
      }
    } catch (error) {
      Logger.instance.error("Error happened when retrieving temaplates gallery data.", { error });
    }
  }

  if (feedData && feedData.keywords) {
    return (
      <BoundedContent>
        <Box m={4}>
          <H3 fontSkin="title-section">More designs</H3>
          <Carousel
            slidesToShow={4}
            gridGutters
            accessibleTextForPrevious="Go to previous slide"
            accessibleTextForNext="Go to next slide"
          >
            {content
              .filter(x => filterAspectRatioBySize(x.previewInfo.aspectRatio, selections!["Size"]))
              .map((elem, index) => (
                <CarouselSlide key={index}>
                  <Link href={elem.studioUrl}>
                    <Card fullBleed overflow="hidden">
                      <FluidImage src={elem.previewUrls.size1x} alt={elem.altText} />
                    </Card>
                  </Link>
                </CarouselSlide>
              ))}
          </Carousel>
        </Box>
      </BoundedContent>
    );
  }

  return null;
};

function filterAspectRatioBySize(aspectRatio: number, size?: string): boolean {
  if (!size) return true;

  if (size.includes("Square")) {
    return aspectRatio === 1;
  }

  if (size.includes("Standard") || size.includes("Slim")) {
    return aspectRatio > 1;
  }

  return false;
}

export default TemplatesGallery;
